import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AppIcon, ICON_OPTIONS } from "@/components/AppIcon";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";

const empty = { name: "", description: "", icon: "AppWindow", url: "", app_type: "external", color: "#1B2380", section: "operative" };
const COLORS = ["#1B2380", "#0E8C84", "#F39200", "#5E9732", "#2A62A0", "#8B5CF6", "#EF4444", "#06B6D4", "#EAB308"];
const SECTION_LABELS = { operative: "Operative (Le tue app)", area_riservata: "Area Riservata", wip: "Work in Progress", gestione_agenzia: "Gestione Agenzia", benefit: "Progetti Benefit", strumenti_digitali: "Strumenti Digitali", contabilita_coop: "Contabilità Cooperativa", direzionale: "Direzionale" };
const CONTAINER_KEYS = ["strumenti_digitali", "contabilita_coop", "direzionale"];

export default function AppsTab({ apps, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState({});
  const [checking, setChecking] = useState(false);
  const [secForm, setSecForm] = useState({});
  const [secSaving, setSecSaving] = useState(false);

  useEffect(() => {
    api.get("/apps/sections").then(({ data }) => setSecForm(data)).catch(() => {});
  }, []);

  const saveSections = async () => {
    setSecSaving(true);
    try {
      const sections = {};
      CONTAINER_KEYS.forEach((k) => { sections[k] = { label: secForm[k]?.label || "", description: secForm[k]?.description || "" }; });
      await api.put("/apps/sections", { sections });
      toast.success("Nomi delle card aggiornati");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSecSaving(false); }
  };

  const loadHealth = useCallback(async (refresh = false) => {
    setChecking(true);
    try {
      const { data } = await api.get("/apps/health", { params: refresh ? { refresh: true } : {} });
      setHealth(data);
    } catch { /* noop */ }
    finally { setChecking(false); }
  }, []);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/apps/${editing.app_id}`, form);
        toast.success("App aggiornata");
      } else {
        await api.post("/apps", form);
        toast.success("App aggiunta");
      }
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/apps/${a.app_id}`);
      toast.success("App eliminata");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="container-sections-editor">
        <h3 className="font-heading text-lg text-slate-900">Card contenitore della home</h3>
        <p className="text-sm text-slate-500 mb-4">Rinomina le card che raggruppano gli strumenti nella home. Non modifica permessi o contenuti.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CONTAINER_KEYS.map((k) => (
            <div key={k} className="space-y-2 rounded-lg border border-slate-100 bg-slate-50 p-3">
              <Label className="text-xs text-slate-500">{SECTION_LABELS[k]}</Label>
              <Input data-testid={`section-label-${k}`} value={secForm[k]?.label || ""} onChange={(e) => setSecForm((f) => ({ ...f, [k]: { ...f[k], label: e.target.value } }))} placeholder="Nome card" />
              <Textarea data-testid={`section-desc-${k}`} value={secForm[k]?.description || ""} onChange={(e) => setSecForm((f) => ({ ...f, [k]: { ...f[k], description: e.target.value } }))} placeholder="Descrizione breve" className="text-sm" rows={2} />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <Button data-testid="save-sections-btn" onClick={saveSections} disabled={secSaving} className="brand-gradient text-white border-0">
            {secSaving ? "Salvataggio…" : "Salva nomi card"}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg text-slate-900">Gestione app</h3>
          <p className="text-sm text-slate-500">{apps.length} applicazioni configurate</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="check-links-btn" onClick={() => loadHealth(true)} disabled={checking} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${checking ? "animate-spin" : ""}`} /> Verifica link
          </Button>
          <Button data-testid="add-app-btn" onClick={openNew} className="brand-gradient text-white border-0">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi app
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {apps.map((a) => (
          <div key={a.app_id} data-testid={`admin-app-${a.app_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${a.color}1A`, color: a.color }}>
                <AppIcon name={a.icon} className="h-5 w-5" />
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-900" onClick={() => openEdit(a)} data-testid={`edit-app-${a.app_id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" data-testid={`delete-app-${a.app_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare {a.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        L'app verrà rimossa e revocata a tutti gli utenti.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(a)} className="bg-red-600 hover:bg-red-700 text-white">Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-heading font-medium text-slate-900">{a.name}</h4>
              <Badge variant="outline" className={a.app_type === "integrated" ? "border-brand-green/40 text-brand-green" : "text-slate-500"}>
                {a.app_type === "integrated" ? "integrata" : "esterna"}
              </Badge>
              <Badge variant="outline" className="text-slate-500">{SECTION_LABELS[a.section] || "Operative (Le tue app)"}</Badge>
            </div>
            <p className="text-sm text-slate-500 line-clamp-2 mb-2">{a.description}</p>
            <div className="flex items-center gap-1.5">
              {a.app_type === "external" && health[a.app_id] && (
                <span
                  data-testid={`app-health-${a.app_id}`}
                  title={health[a.app_id].status === "online" ? `Raggiungibile (HTTP ${health[a.app_id].code})` : health[a.app_id].status === "offline" ? `Non raggiungibile${health[a.app_id].code ? ` (HTTP ${health[a.app_id].code})` : ""}` : "URL non impostato"}
                  className={`inline-flex items-center gap-1 shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded-full ${health[a.app_id].status === "online" ? "bg-emerald-50 text-emerald-700" : health[a.app_id].status === "offline" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-400"}`}
                >
                  <span className={`h-2 w-2 rounded-full ${health[a.app_id].status === "online" ? "bg-emerald-500" : health[a.app_id].status === "offline" ? "bg-red-500" : "bg-slate-400"}`} />
                  {health[a.app_id].status === "online" ? "attivo" : health[a.app_id].status === "offline" ? "non raggiungibile" : "n/d"}
                </span>
              )}
              <p className="text-xs text-slate-400 truncate">{a.url}</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifica app" : "Aggiungi app"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input data-testid="app-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea data-testid="app-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input data-testid="app-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.app_type} onValueChange={(v) => setForm({ ...form, app_type: v })}>
                  <SelectTrigger data-testid="app-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">Esterna (nuova scheda)</SelectItem>
                    <SelectItem value="integrated">Integrata (nella Suite)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Icona</Label>
                <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                  <SelectTrigger data-testid="app-icon-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {ICON_OPTIONS.map((ic) => (
                      <SelectItem key={ic} value={ic}>
                        <span className="flex items-center gap-2"><AppIcon name={ic} className="h-4 w-4" /> {ic}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sezione home {editing?.section_locked && <span className="text-xs text-orange-600">· bloccata</span>}</Label>
              <Select value={form.section || "operative"} onValueChange={(v) => setForm({ ...form, section: v })} disabled={!!editing?.section_locked}>
                <SelectTrigger data-testid="app-section-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operative">Operative (Le tue app)</SelectItem>
                  <SelectItem value="area_riservata">Area Riservata</SelectItem>
                  <SelectItem value="wip">Work in Progress</SelectItem>
                  <SelectItem value="gestione_agenzia">Gestione Agenzia</SelectItem>
                  <SelectItem value="benefit">Progetti Benefit</SelectItem>
                  <SelectItem value="strumenti_digitali">Strumenti Digitali</SelectItem>
                  <SelectItem value="contabilita_coop">Contabilità Cooperativa</SelectItem>
                  <SelectItem value="direzionale">Direzionale</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">Raggruppamento visivo nella home. Non modifica permessi o accessi.{editing?.section_locked && " Questa app è bloccata e non può essere spostata."}</p>
            </div>
            <div className="space-y-2">
              <Label>Colore accento</Label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`h-8 w-8 rounded-full border-2 transition-all ${form.color === c ? "border-slate-900 scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    data-testid={`color-${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-app-btn" onClick={save} disabled={saving || !form.name || !form.url} className="brand-gradient text-white border-0">
              {saving ? "Salvataggio…" : editing ? "Salva modifiche" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
